import { fmtDate } from "@/lib/dt";   // 5c · F-5
import React, { useCallback, useEffect, useRef, useState } from "react";
import DateInput from "@/components/DateInput";
import { useEngagement } from "@/contexts/EngagementContext";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDuration } from "@/lib/format";
import api, { toErrMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Clock, Coffee, Play, Plus, Trash2, Moon } from "lucide-react";
import { toast } from "sonner";
import { asArray } from "@/lib/asArray";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { engagementName } from "@/lib/engagementLabel";   // Batch 15 · N5 (F-119)

const IDLE_THRESHOLD_MS = 5 * 60 * 1000; // 5 min

/**
 * TimeChip — top-bar chip that shows the SIGNED-IN USER'S OWN time on the
 * currently-selected engagement + a break toggle + a manual-entry form.
 *
 * Access-scoping (matches spec point 6):
 *   • Reads only from  GET /api/time/{eng_id}/mine  (server strips grade/rate).
 *   • Never displays other users' hours, rates, fees, budgets, or margin.
 *   • Break/manual endpoints act only on the logged-in user's own row.
 *
 * Idle notice (Feb 15, 2026 request):
 *   After IDLE_THRESHOLD_MS with no mouse/keyboard/scroll activity, we surface
 *   a subtle "Away from keyboard" badge on the chip so the user knows
 *   auto-tracking will pause the moment their tab closes or browser sleeps.
 *   Activity anywhere in the app resets the timer. This is a HINT only — we
 *   do NOT force a server-side break; existing 90s WS stale-out already
 *   handles the true pause path safely.
 */
export default function TimeChip() {
  const { user } = useAuth();
  const { lang, t } = useI18n();
  const { currentEngId, current } = useEngagement();
  const [data, setData] = useState(null);
  const [manualMin, setManualMin] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [manualDate, setManualDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [breaking, setBreaking] = useState(false);
  const [, setTick] = useState(0);
  const [idle, setIdle] = useState(false);
  const lastActivityRef = useRef(Date.now());

  const load = useCallback(async () => {
    if (!currentEngId) return;
    try {
      const r = await api.get(`/time/${currentEngId}/mine`, { _silent: true });
      setData(r.data);
    } catch { /* stay silent — presence WS may still be establishing */ }
  }, [currentEngId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!currentEngId) return;
    const id = setInterval(() => { setTick((n) => n + 1); load(); }, 30_000);
    return () => clearInterval(id);
  }, [currentEngId, load]);

  // ----- Idle detection -----
  useEffect(() => {
    const bump = () => {
      lastActivityRef.current = Date.now();
      if (idle) setIdle(false);
    };
    const events = ["mousemove", "keydown", "mousedown", "wheel", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const check = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      setIdle(elapsed >= IDLE_THRESHOLD_MS);
    }, 15_000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(check);
    };
  }, [idle]);

  if (!currentEngId || !user) return null;

  const totalSecs = data?.total_seconds || 0;
  const st = data?.state || {};
  let liveSecs = 0;
  if (st.active && st.session_started_at) {
    const startedMs = new Date(st.session_started_at).getTime();
    const now = Date.now();
    liveSecs = Math.max(0, Math.floor((now - startedMs) / 1000));
    if (st.on_break && st.break_started_at) {
      const bStart = new Date(st.break_started_at).getTime();
      liveSecs = Math.max(0, liveSecs - Math.floor((now - bStart) / 1000));
    }
  }
  const displaySecs = totalSecs + liveSecs;
  const label = fmtDuration(Math.floor(displaySecs / 60), lang);

  const toggleBreak = async () => {
    setBreaking(true);
    try {
      if (st.on_break) {
        await api.post("/time/break/stop", { engagement_id: currentEngId });
        toast.success(t("timeChip.breakEnded"));
      } else {
        await api.post("/time/break/start", { engagement_id: currentEngId });
        toast.success(t("timeChip.onBreakToast"));
      }
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("timeChip.breakFailed"))); }
    finally { setBreaking(false); }
  };

  const addManual = async () => {
    const mins = Number(manualMin);
    if (!mins || mins <= 0) { toast.error(t("timeChip.enterMinutes")); return; }
    setSaving(true);
    try {
      await api.post("/time/manual", {
        engagement_id: currentEngId, minutes: mins, entry_date: manualDate, note: manualNote,
      });
      toast.success(t("timeChip.addedMin").replace("{n}", String(mins)));
      setManualMin(""); setManualNote("");
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("timeChip.addFailed"))); }
    finally { setSaving(false); }
  };

  const deleteEntry = async (id) => {
    if (!await askConfirm(t("timeChip.deleteConfirm"))) return;
    try {
      await api.delete(`/time/entries/${id}`);
      toast.success(t("timeChip.deleted"));
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("timeChip.deleteFailed"))); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`gap-1.5 h-9 ${idle && st.active && !st.on_break ? "border border-amber-300 bg-amber-50 dark:bg-amber-950/30" : ""}`}
          data-testid="time-chip"
          title={idle
            ? t("timeChip.idleHint")
            : t("timeChip.titleFor").replace("{name}", (current && engagementName(current, t)) || t("timeChip.thisEng"))}
        >
          <Clock className="h-3.5 w-3.5 text-slate-500" />
          <span className="text-xs font-medium tabular-nums" data-testid="time-chip-total">{label}</span>
          {st.on_break && (
            <Badge variant="outline" className="ms-1 text-[9px] text-amber-700 border-amber-300">{t("timeChip.onBreak")}</Badge>
          )}
          {idle && st.active && !st.on_break && (
            <span className="ms-1 inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                  data-testid="time-chip-idle-badge">
              <Moon className="h-2.5 w-2.5" /> {t("timeChip.away")}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96" data-testid="time-chip-panel">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">{t("timeChip.panelTitle")}</div>
              <div className="text-[11px] text-muted-foreground">{t("timeChip.panelDesc")}</div>
            </div>
            <Button size="sm" variant={st.on_break ? "default" : "outline"}
                    onClick={toggleBreak} disabled={breaking || !st.active}
                    className={st.on_break ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                    data-testid="time-break-toggle">
              {st.on_break
                ? <><Play className="me-1 h-3 w-3" /> {t("timeChip.endBreak")}</>
                : <><Coffee className="me-1 h-3 w-3" /> {t("timeChip.takeBreak")}</>}
            </Button>
          </div>
          {idle && st.active && !st.on_break && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                 data-testid="time-chip-idle-notice">
              <Moon className="mt-0.5 h-3.5 w-3.5 flex-none" />
              <div>
                <div className="font-medium">{t("timeChip.awayHeader")}</div>
                <div>{t("timeChip.awayLong")}</div>
              </div>
            </div>
          )}
          {!st.active && (
            <div className="rounded-md border bg-slate-50 p-2 text-[11px] text-muted-foreground dark:bg-slate-900/40">
              {t("timeChip.autoTrackNotice")}
            </div>
          )}
          <div className="rounded-md border p-2">
            <div className="text-[11px] uppercase text-muted-foreground">{t("timeChip.totalLogged")}</div>
            <div className="text-xl font-semibold tabular-nums">{label}</div>
          </div>

          <div className="space-y-2 rounded-md border bg-slate-50 p-2 dark:bg-slate-900/40">
            <div className="text-xs font-medium">{t("timeChip.addManual")}</div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">{t("timeChip.date")}</Label>
                <DateInput value={manualDate} onChange={(v) => setManualDate(v)}
                       className="h-8 text-xs" data-testid="time-manual-date" />
              </div>
              <div>
                <Label className="text-[10px]">{t("timeChip.minutes")}</Label>
                <Input type="number" min="1" max="1440" value={manualMin}
                       onChange={(e) => setManualMin(e.target.value)}
                       className="h-8 text-xs" placeholder={t("timeChip.minutesPh")}
                       data-testid="time-manual-minutes" />
              </div>
            </div>
            <Input value={manualNote} onChange={(e) => setManualNote(e.target.value)}
                   placeholder={t("timeChip.notePh")} className="h-8 text-xs"
                   data-testid="time-manual-note" />
            <Button size="sm" onClick={addManual} disabled={saving} className="w-full"
                    data-testid="time-manual-add">
              <Plus className="me-1 h-3 w-3" />{saving ? t("timeChip.savingBtn") : t("timeChip.addEntry")}
            </Button>
          </div>

          <div className="max-h-60 overflow-y-auto">
            <div className="mb-1 text-[11px] uppercase text-muted-foreground">{t("timeChip.recent")}</div>
            {(!data?.entries || data.entries.length === 0) && (
              <div className="text-[11px] text-muted-foreground">{t("timeChip.noEntries")}</div>
            )}
            {asArray(data?.entries).slice(0, 12).map((e) => (
              <div key={e.id} className="flex items-center justify-between border-b py-1 text-[11px]">
                <div>
                  <span className="tabular-nums font-medium">{(e.seconds / 60).toFixed(0)} {t("timeChip.minSuffix")}</span>
                  <span className="ms-2 text-muted-foreground">{fmtDate(e.started_at)}</span>
                  {e.source === "manual" && <Badge variant="outline" className="ms-1 text-[9px]">{t("timeChip.manualBadge")}</Badge>}
                  {e.note && <span className="ms-1 text-muted-foreground italic">— {e.note}</span>}
                </div>
                <Button size="icon" variant="ghost" className="h-6 w-6"
                        onClick={() => deleteEntry(e.id)}
                        data-testid={`time-entry-delete-${e.id}`}>
                  <Trash2 className="h-3 w-3 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
